/**
 * ``useRecentTracksFeed`` — the Tracks panel's global REST feed (ADR-041;
 * REQ-UI-170; REQ-QUERY-050).
 *
 * Polls ``GET /api/query/recent-tracks`` — live tracks (two or more members)
 * across all Producers, newest head first, bounded by the deployment's live
 * window (the BFF's default; the client passes none so the two agree) — and
 * seeds their **summaries** into the shared object-track store. Members ride
 * the viewport backfill (:func:`map/use-object-tracks-feed`) and the live SSE
 * bridge; this feed is what lets the panel list a track before the map has
 * been panned to it.
 *
 * Mirrors :func:`detections/use-recent-detections-feed` (cadence, role gate,
 * shape validation, retry posture).
 *
 * Cross-references:
 *   - REQ-UI-170 (the Tracks panel); REQ-UI-181 (the live window).
 *   - REQ-QUERY-050 (the route).
 *   - object-tracks/object-track-store.ts (the buffer this seeds).
 */
import { useEffect } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useSession } from "@/auth/session-context";
import { apiFetch, ApiError } from "@/lib/api-client";
import { useLiveWindowMs } from "@/chrome/use-deployment-config";
import type { Role } from "@/auth/session-types";
import type { components } from "@/lib/api-types.gen";
import { useObjectTrackStore } from "@/object-tracks/object-track-store";

export type RecentTracksResponse = components["schemas"]["RecentTracksResponse"];

/** Every read role sees the map, so every read role sees tracks (REQ-AUTH-075). */
export const RECENT_TRACKS_FEED_READER_ROLES: readonly Role[] = [
  "operator",
  "analyst",
  "admin",
  "support_engineer",
];

/** Poll cadence — the detections feed's, so the two panels refresh together. */
export const RECENT_TRACKS_FEED_REFETCH_MS = 10_000;

/** The route's maximum; the buffer cap is larger, so nothing is dropped here. */
export const RECENT_TRACKS_FEED_LIMIT = 100;

export const RECENT_TRACKS_FEED_QUERY_KEY = ["recent-tracks-feed"] as const;

export function useRecentTracksFeed(): UseQueryResult<RecentTracksResponse, ApiError> {
  const session = useSession();
  const enabled = session.hasAnyRole(RECENT_TRACKS_FEED_READER_ROLES);
  const retentionMs = useLiveWindowMs();
  const query = useQuery<RecentTracksResponse, ApiError>({
    queryKey: RECENT_TRACKS_FEED_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const raw = await apiFetch<unknown>(
        `/api/query/recent-tracks?limit=${RECENT_TRACKS_FEED_LIMIT}`,
        { signal },
      );
      if (!isResponseShape(raw)) {
        console.error(
          "[meridian/object-tracks/use-recent-tracks-feed] response failed shape validation",
          raw,
        );
        throw new ApiError(500, raw, "recent-tracks response did not match the expected shape");
      }
      return raw;
    },
    enabled,
    refetchInterval: RECENT_TRACKS_FEED_REFETCH_MS,
    staleTime: 0,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // Seed the shared buffer on each distinct response (TanStack hands back a
  // stable reference for an unchanged cache entry). The prune runs on the
  // deployment live window so the panel and the map agree on "recent".
  const data = query.data;
  useEffect(() => {
    if (data !== undefined) {
      useObjectTrackStore.getState().seedSummaries(data.items, { retentionMs });
    }
  }, [data, retentionMs]);

  return query;
}

function isResponseShape(value: unknown): value is RecentTracksResponse {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    Array.isArray(obj.items) &&
    typeof obj.truncated === "boolean" &&
    typeof obj.duration_ms === "number"
  );
}
